import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { sniperGuess } from '../../redux/pupApi';
import './SniperGuessPanel.css';

// Same board-order rank buttons as the Go Fish picker; "10" is the server's wire form.
const VALUE_RANKS = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];

// The Stream Sniper permanent's on-demand action: while exactly one other unfolded player
// remains, its owner may — once per round — guess the RANKS of all that opponent's hole
// cards (suits/order don't matter). GameTable mounts this only while the guess is available
// (hero owns the card, heads-up, round live, no Power-Up resolving, guess unspent), so this
// panel only manages its own open/closed state and the slot-filling. `count` = the
// opponent's live hole-card count (an EXTRA_CARD can grow it past the variant's 2).
//
// `anchorStyle` is the felt position of the hero's OWN Stream Sniper permanent
// (useTableGeometry.permanentStyle — a { bottom, left, transform } that centers an element
// on the card). The closed button reuses it verbatim, so the button sits centered ON the
// card it belongs to (and is width-clamped to the card in CSS); the open rank panel is too
// tall to center there, so it keeps the horizontal centering and grows UPWARD from that
// point. Absent (geometry not ready yet) → both fall back to the CSS corner dock.
const SniperGuessPanel = ({ roomId, targetName, count, anchorStyle }) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [ranks, setRanks] = useState([]);
  const [error, setError] = useState(null);
  const submitRef = useRef(false);

  // A new target/hole-count (a fold ended one heads-up moment, another began; an extra card
  // landed) resets the half-filled guess — its slots no longer match.
  useEffect(() => { setRanks([]); }, [targetName, count]);

  const addRank = (rank) => {
    if (ranks.length >= count) return;
    setRanks((r) => [...r, rank]);
  };

  const removeSlot = (idx) => setRanks((r) => r.filter((_, i) => i !== idx));

  const submit = async () => {
    if (submitRef.current || ranks.length !== count) return;
    submitRef.current = true;
    setError(null);
    try {
      await dispatch(sniperGuess({ roomId, ranks })).unwrap();
      // The room-wide result rides PUP_CARDS_REVEALED (toasted); the fulfilled reducer sets
      // sniperGuessUsed so GameTable unmounts this panel.
    } catch (e) {
      setError(typeof e === 'string' && e ? e : 'Could not take the shot');
      setTimeout(() => setError(null), 4000);
      submitRef.current = false;
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        className="sniper-guess-open"
        style={anchorStyle}
        onClick={() => setOpen(true)}
        // The label is deliberately just "🎯 Guess" — it sits on the Sniper card, and the
        // target is unambiguous (the guess only exists heads-up). The name lives here.
        title={`Stream Sniper: guess ${targetName}'s cards (once per round)`}
      >
        🎯 Guess
      </button>
    );
  }

  return (
    <div
      className="sniper-guess-panel"
      style={anchorStyle
        // Same anchor point, but bottom-aligned instead of centered: the panel grows up
        // over the felt rather than off the bottom of the table.
        ? { left: anchorStyle.left, bottom: anchorStyle.bottom, transform: 'translateX(-50%)' }
        : undefined}
    >
      <div className="sniper-guess-title">
        🎯 Name all {count} of {targetName}'s card values
      </div>
      <div className="sniper-guess-slots">
        {Array.from({ length: count }, (_, i) => (
          <button
            key={i}
            type="button"
            className={`sniper-guess-slot${ranks[i] ? ' sniper-guess-slot-filled' : ''}`}
            onClick={() => ranks[i] && removeSlot(i)}
            title={ranks[i] ? 'Click to clear' : 'Pick a value below'}
          >
            {ranks[i] || '?'}
          </button>
        ))}
      </div>
      <div className="sniper-guess-ranks">
        {VALUE_RANKS.map((rank) => (
          <button
            key={rank}
            type="button"
            className="pup-value-picker-btn"
            disabled={ranks.length >= count}
            onClick={() => addRank(rank)}
          >
            {rank}
          </button>
        ))}
      </div>
      <div className="sniper-guess-actions">
        <button
          type="button"
          className="pup-optin-btn"
          disabled={ranks.length !== count}
          onClick={submit}
        >
          Take the shot
        </button>
        <button
          type="button"
          className="pup-optin-btn sniper-guess-cancel"
          onClick={() => { setOpen(false); setRanks([]); }}
        >
          Not yet
        </button>
      </div>
      {error && <div className="sniper-guess-error">{error}</div>}
    </div>
  );
};

export default SniperGuessPanel;
